import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import StandardDialog from "components/UI/Dialog/StandardDialog";
import ProfileIcon from "components/UI/icons/ProfileIcon";
import SendItIcon from "components/UI/icons/SendItIcon";
import VegaChatIcon from "components/UI/icons/VegaChatIcon";
import COLORS from 'lib/colors';
import { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import Link from "@mui/material/Link";
import { downloadData } from "lib/utils";
import Papa from 'papaparse';
import IconButton from "@mui/material/IconButton";

function filterMessagesForChat(messages) {
  return messages.filter(message => !message.content.startsWith('###interpretation:'))
}

function ChatInput({onSubmit, loading}) {
  const [inputValue, setInputValue] = useState("")

  const handleSend = () => {
    if (inputValue.trim()) {
      onSubmit(inputValue)
      setInputValue(""); // Clear input field after sending
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSend();
    }
  };

  return (
    <TextField
      id="chat-input"
      sx={{ width: "100%", borderColor: COLORS.cadetGray }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start" sx={{ "& > svg": { width: 36, height: 36 } }}>
            <ProfileIcon />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment
            position="end"
            sx={{ color: COLORS.frenchBlue, cursor: "pointer" }}
            onClick={handleSend}
          >
            <SendItIcon />
          </InputAdornment>
        ),
      }}
      placeholder="Ask a question..."
      variant="outlined"
      value={inputValue}
      onChange={(e) => setInputValue(e.target.value)}
      onKeyDown={handleKeyDown} // Capture Enter key press
      disabled={loading}
    />
  );
}

function ErrorChat({message}) {

  return(
    <Stack direction='row' gap={1.5} sx={{ mr: '40px', color: COLORS.copperRed }}>
      <VegaChatIcon/>
      <Stack direction='column' gap={1} sx={{
        '& > p': { m: 0, lineHeight: '22px' },
      }}>
        <p>{message.content}</p>
      </Stack>
    </Stack>
  )
}

function ResponsiveImage({ src, alt }) {
  return (
    <Box
      sx={{
        width: 'auto',
        maxWidth: '100%',
        height: '180px',
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <img
        src={src}
        alt={alt}
        style={{
          height: '100%',
          width: 'auto',
          objectFit: 'contain',
        }}
      />
    </Box>
  )
}

function ImageResponse({src}) {
  return (
    <Stack direction='column' gap={2}>
      <Stack direction='row' gap={1} sx={{ color: COLORS.frenchBlue }}>
        <Link href={src} download sx={{ color: COLORS.frenchBlue }} >
          Click to download png.
        </Link>
        <IconButton href={src} download aria-label='Download PNG' sx={{ color: COLORS.frenchBlue, width: '30px', height: '30px', mt: '-4px', ml: '-2px' }}>
          <FileDownloadOutlinedIcon/>
        </IconButton>
      </Stack>
      <ResponsiveImage src={src} alt='image'/>
    </Stack>
  )
}

function TableResponse({table}) {
  const handleDownload = useCallback(() => {
    downloadData(Papa.unparse(table), 'vega-ai-insight.csv', 'text/csv')
  }, [table])

  return(
    <Stack direction='row' gap={1} sx={{ color: COLORS.frenchBlue }}>
      <Link onClick={handleDownload} sx={{ color: COLORS.frenchBlue }} >
        Click to download table.
      </Link>
      <IconButton onClick={handleDownload} aria-label='Download Table' sx={{ color: COLORS.frenchBlue, width: '30px', height: '30px', mt: '-4px', ml: '-2px' }}>
        <FileDownloadOutlinedIcon/>
      </IconButton>
    </Stack>
  )
}

function AiResponse({message, loading=false}) {
  const withMarkdownNewlines = message.content.replace(/\n/g, '  \n')

  let content
  if (message.table) {
    content = <TableResponse table={message.table} />
  } else if (message.images?.length) {
    content = (
      <Stack direction="column" gap={1}>
        {message.images.map((image, index) => (
          <ImageResponse key={index} src={image} />
        ))}
      </Stack>
    )
  } else {
    content = <ReactMarkdown>{withMarkdownNewlines}</ReactMarkdown>;
  }

  return(
    <Stack direction='row' gap={1.5} sx={{ mr: '40px' }}>
      <Box sx={{ color: COLORS.frenchBlue}}><VegaChatIcon/></Box>
      <Stack direction='column' gap={1} sx={{
        color: loading ? COLORS.cadetGray : undefined,
        mt: '2px',
        '& > p': { m: 0, lineHeight: '22px' },
        '& li': { lineHeight: '22px' },
      }}>
        {content}
      </Stack>
    </Stack>
  )
}

function UserQuestion({message}) {

  return(
    <Stack direction='row-reverse' gap={1.5} sx={{ ml: '80px' }}>
      <ProfileIcon />
      <Chip label={message.content}
        sx={{
          height: 'auto',
          '& .MuiChip-label': {
            display: 'block',
            whiteSpace: 'normal',
            fontSize: '16px',
          },
        }}
      />
    </Stack>
  )
}

function DefaultText({onSubmit}) {
  const handleClick = (inputValue) => {
    if (inputValue.trim()) {
      onSubmit(inputValue)
    }
  };

  const suggestedPrompts = [
    'What are the top 5 states by started leads?',
    'What are the top 5 programs by application rate?',
    'Which source has the best start rate?',
  ]

  return(
    <Box>
      <Box sx={{ fontSize: '12px', color: COLORS.charcoal, lineHeight: '15px'}}>
        While this chat tool provides valuable insights for informational and analytical purposes, artificial intelligence (AI) is an
        emerging technology and results may vary based on user input. Users should verify results independently and consult experts for critical decisions.
      </Box>
      <Divider flexItem sx={{ mt: '8px', mb: '12px' }}/>
      <Stack direction='row' gap={1.5} sx={{ mr: '40px', mt: '2px' }}>

        <Box sx={{ color: COLORS.frenchBlue}}><VegaChatIcon/></Box>
        <Stack direction='column' gap={1} sx={{
          mt: '2px',
          '& > p': { m: 0, lineHeight: '22px' },
          '& > .MuiChip-root': { width: 'fit-content', px: '8px', py: '3px', fontSize: '16px' }
        }}>
          <p>Hello! Welcome to the VEGA AI Lead Analysis tool.</p>
          <p>Ask a question to get started, or choose one of the suggested prompts below:</p>
          {
            suggestedPrompts.map((text, index) => (
              <Chip
                key={index}
                label={text}
                onClick={() => handleClick(text)}
              />
            ))
          }
        </Stack>
      </Stack>
    </Box>
  )
}

function ChatBody({ messages, onSubmit }) {
  const stackRef = useRef<HTMLDivElement | null>(null)
  const filteredMessages = filterMessagesForChat(messages)

  const scrollLastMessageToTop = () => {
    const stack = stackRef.current
    if (stack) {
      const lastMessage = stack.lastElementChild?.previousElementSibling as HTMLElement | null
      const lastMessageTop = lastMessage?.offsetTop || 0

      // TODO - handle this systematically at some point
      const arbitraryOffset = -115

      stack.scrollTo({
        top: lastMessageTop + arbitraryOffset,
        behavior: 'smooth',
      })
    }
  }

  useEffect(() => {
    scrollLastMessageToTop();
  }, [filteredMessages]);

  return (
    <Stack
      ref={stackRef}
      direction="column"
      gap={2}
      sx={{
        flexGrow: 1,
        p: 2,
        borderRadius: '8px',
        backgroundColor: COLORS.backgroundGray,
        overflowY: 'auto',
      }}
    >
      <DefaultText onSubmit={onSubmit} />
      <Divider flexItem />
      {filteredMessages.map((message, index) => {
        if (message.role === 'assistant') {
          return <AiResponse key={index} message={message} />
        } else if (message.role === 'user') {
          return <UserQuestion key={index} message={message} />
        } else if (message.role === 'error') {
          return <ErrorChat key={index} message={message} />
        } else if (message.role === 'loading') {
          return <AiResponse key={index} message={message} loading={true} />
        } else {
          console.log('message')
          return null // Handle any unexpected roles if necessary
        }
      })}
    </Stack>
  );
}

export default function AiChatDialog({onClose, messages, onChatSubmit, loading}) {

  return(
    <StandardDialog
      title='VEGA AI Lead Analysis'
      description='This AI chat can be minimized. If the Lead Table dataset is updated, please Launch a new AI Analysis to reflect the changes in this chat.'
      onCancel={onClose}
      maxWidth='600px'
    >
      <Stack direction='column' sx={{ height: '320px', width: '100%', gap: 2 }}>
        <ChatBody onSubmit={onChatSubmit} messages={messages} />
        <ChatInput onSubmit={onChatSubmit} loading={loading} />
      </Stack>
    </StandardDialog>
  )
}
